import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Minus, Plus, ShoppingBag, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useCart } from '@/state/cart';
import { useCatalog } from '@/state/catalog';
import { formatKr, formatNumber } from '@/lib/api';
import { productImage } from '@/lib/images';
import { Picture } from './Picture';

export function cartLines(items, bySlug) {
  return items
    .map((i) => {
      const p = bySlug[i.slug];
      if (!p) return null;
      const price = p.pricePerTonne;
      return { ...i, product: p, lineTotal: price !== null && price !== undefined ? price * i.quantityTonnes : null };
    })
    .filter(Boolean);
}

export function QuantityStepper({ value, onChange, min = 0.5, step = 1, testId, size = 'md' }) {
  const h = size === 'lg' ? 'h-12' : 'h-10';
  const set = (v) => onChange(Math.max(min, Math.round(v * 10) / 10));
  return (
    <div className={`inline-flex ${h} items-stretch overflow-hidden rounded-[10px] border border-[var(--hr-border)] bg-white`} data-testid={testId}>
      <button type="button" onClick={() => set(value - step)} aria-label="Reduser mengde" className="flex w-10 items-center justify-center text-[var(--hr-muted)] transition-colors duration-150 hover:bg-[var(--hr-inset)] hover:text-[var(--hr-heading)] focus-visible:outline-none focus-visible:bg-[var(--hr-accent-soft)]" data-testid={testId ? `${testId}-minus` : undefined}>
        <Minus className="h-4 w-4" aria-hidden="true" />
      </button>
      <input
        type="number"
        inputMode="decimal"
        min={min}
        step="0.5"
        value={value}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        onBlur={() => set(Number(value) || min)}
        aria-label="Mengde i tonn"
        className={`hr-tabular w-[72px] border-x border-[var(--hr-hairline)] bg-transparent text-center font-semibold text-[var(--hr-heading)] outline-none ${size === 'lg' ? 'text-[18px]' : 'text-[15px]'}`}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      <button type="button" onClick={() => set((Number(value) || 0) + step)} aria-label="Øk mengde" className="flex w-10 items-center justify-center text-[var(--hr-muted)] transition-colors duration-150 hover:bg-[var(--hr-inset)] hover:text-[var(--hr-heading)] focus-visible:outline-none focus-visible:bg-[var(--hr-accent-soft)]" data-testid={testId ? `${testId}-plus` : undefined}>
        <Plus className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

/** Handlekurv i skuff fra høyre. */
export function CartSheet() {
  const cart = useCart();
  const { bySlug } = useCatalog();
  const navigate = useNavigate();
  const lines = cartLines(cart.items, bySlug);
  const total = lines.reduce((s, l) => s + (l.lineTotal ?? 0), 0);
  const tonnes = lines.reduce((s, l) => s + (Number(l.quantityTonnes) || 0), 0);

  return (
    <Sheet open={cart.open} onOpenChange={cart.setOpen}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-[440px]" data-testid="cart-sheet">
        <SheetHeader className="border-b border-[var(--hr-hairline)] px-6 py-5 text-left">
          <SheetTitle className="font-display text-[20px]">Din bestilling</SheetTitle>
          <SheetDescription>Mengder i tonn. Priser eks. mva – frakt avtales ved bekreftelse.</SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="cart-items">
          {lines.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 py-16 text-center" data-testid="cart-empty">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--hr-inset)] text-[var(--hr-muted)]">
                <ShoppingBag className="h-5 w-5" aria-hidden="true" />
              </span>
              <p className="text-[15px] font-medium text-[var(--hr-heading)]">Ingen produkter ennå</p>
              <p className="max-w-[28ch] text-[14px] text-[var(--hr-muted)]">Velg et produkt og mengde, så samler vi bestillingen her.</p>
              <Button variant="outline" className="mt-2 h-10 rounded-[10px]" onClick={() => { cart.setOpen(false); navigate('/produkter'); }} data-testid="cart-browse-button">
                Se produkter
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-[var(--hr-hairline)]">
              {lines.map((l) => (
                <li key={l.slug} className="flex gap-4 py-4" data-testid={`cart-line-${l.slug}`}>
                  <Picture variant={productImage(l.product.image)} sizes="64px" alt="" className="h-16 w-16 shrink-0 rounded-[10px] object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-[15px] font-semibold text-[var(--hr-heading)]">{l.product.name}</p>
                        <p className="hr-tabular text-[13px] text-[var(--hr-muted)]">{l.product.pricePerTonne !== null ? `${formatNumber(l.product.pricePerTonne)} kr/tonn` : 'Prosjektpris'}</p>
                      </div>
                      <button type="button" onClick={() => cart.remove(l.slug)} aria-label={`Fjern ${l.product.name}`} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[var(--hr-muted)] transition-colors duration-150 hover:bg-[var(--hr-red-bg)] hover:text-[var(--hr-red)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={`cart-remove-${l.slug}`}>
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                    <div className="mt-2.5 flex items-center justify-between gap-3">
                      <QuantityStepper value={l.quantityTonnes} onChange={(v) => cart.setQuantity(l.slug, v)} testId={`cart-qty-${l.slug}`} />
                      <span className="hr-tabular text-[15px] font-semibold text-[var(--hr-heading)]" data-testid={`cart-line-total-${l.slug}`}>{l.lineTotal !== null ? formatKr(l.lineTotal) : '–'}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {lines.length > 0 && (
          <div className="border-t border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] px-6 py-5">
            <div className="flex items-center justify-between text-[14px] text-[var(--hr-muted)]">
              <span>{formatNumber(tonnes, 1)} tonn · {lines.length} {lines.length === 1 ? 'produkt' : 'produkter'}</span>
              <span>eks. mva</span>
            </div>
            <div className="mt-1 flex items-baseline justify-between">
              <span className="text-[15px] font-medium text-[var(--hr-heading)]">Estimert sum</span>
              <span className="hr-tabular font-display text-[26px] font-semibold text-[var(--hr-heading)]" data-testid="cart-total">{formatKr(total)}</span>
            </div>
            <Button className="mt-4 h-12 w-full rounded-[12px] bg-[var(--site-orange)] text-[15px] font-semibold text-[var(--site-ink)] shadow-none hover:bg-[var(--site-orange-2)]" onClick={() => { cart.setOpen(false); navigate('/bestill'); }} data-testid="cart-checkout-button">
              Gå til bestilling <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
